import React from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import DescriptionIcon from '@material-ui/icons/Description'
import GetAppIcon from '@material-ui/icons/GetApp'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import EventIcon from '@material-ui/icons/Event'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import { CompanyDocument } from '../../models/Company/types'
import { Container } from '../../components'

interface DocumentViewModalProps {
    isOpen: boolean
    onClose: () => void
    document: CompanyDocument | null
}

export const DocumentViewModal: React.FC<DocumentViewModalProps> = ({
    isOpen,
    onClose,
    document,
}) => {
    const theme = useTheme()

    if (!document) return null

    const formatDate = (dateString: string) => {
        // Parse date parts directly to avoid timezone conversion issues
        const [year, month, day] = dateString.split('T')[0].split('-')
        const months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ]
        return `${months[parseInt(month, 10) - 1]} ${parseInt(
            day,
            10,
        )}, ${year}`
    }

    // Check if the file is a PDF (can be displayed directly in iframe)
    const isPdf = document.name.toLowerCase().endsWith('.pdf')

    // Get the viewer URL - use Google Docs Viewer for non-PDF files
    const getViewerUrl = () => {
        if (!document.document) return ''
        if (isPdf) {
            return document.document
        }
        // Use Google Docs Viewer for DOCX, DOC, TXT, etc.
        return `https://docs.google.com/gview?url=${encodeURIComponent(
            document.document,
        )}&embedded=true`
    }

    const handleDownload = () => {
        if (document.document) {
            const link = window.document.createElement('a')
            link.href = document.document
            link.download = document.name
            link.click()
        }
    }

    const handleOpenInNewTab = () => {
        if (document.document) {
            window.open(document.document, '_blank')
        }
    }

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                style: {
                    height: '90vh',
                    maxHeight: '90vh',
                },
            }}
        >
            {/* Header */}
            <Container
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexDirection: 'row',
                    padding: theme.spacing(2),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: theme.spacing(2) }}
                >
                    <Box
                        style={{
                            height: 40,
                            width: 40,
                            borderRadius: theme.spacing(1),
                            backgroundColor: theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.primary.contrastText,
                        }}
                    >
                        <DescriptionIcon />
                    </Box>
                    <Typography variant="h6">{document.name}</Typography>
                </Box>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Container>

            {/* Content */}
            <DialogContent
                style={{
                    padding: 0,
                    display: 'flex',
                    height: 'calc(90vh - 140px)',
                }}
            >
                {/* Left: Document Details */}
                <Box
                    style={{
                        width: 320,
                        minWidth: 320,
                        borderRight: `1px solid ${theme.palette.divider}`,
                        padding: theme.spacing(3),
                        overflowY: 'auto',
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    {/* Date Info */}
                    <Typography
                        variant="overline"
                        style={{
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            marginBottom: theme.spacing(2),
                            display: 'block',
                        }}
                    >
                        Document Details
                    </Typography>

                    <Box mb={3}>
                        <Typography variant="body2" color="textSecondary">
                            Document type
                        </Typography>
                        <Typography
                            variant="body1"
                            style={{ fontWeight: 500, marginTop: 4 }}
                        >
                            {document.document_type_detail?.name ?? 'General'}
                        </Typography>
                    </Box>

                    <Box mb={3}>
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={0.5}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <EventIcon
                                style={{
                                    fontSize: 18,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                            <Typography variant="body2" color="textSecondary">
                                Created
                            </Typography>
                        </Box>
                        <Typography
                            variant="body1"
                            style={{ fontWeight: 500, marginLeft: 26 }}
                        >
                            {formatDate(document.created_date)}
                        </Typography>
                    </Box>

                    <Box mb={3}>
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={0.5}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <AutorenewIcon
                                style={{
                                    fontSize: 18,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                            <Typography variant="body2" color="textSecondary">
                                Last Modified
                            </Typography>
                        </Box>
                        <Typography
                            variant="body1"
                            style={{ fontWeight: 500, marginLeft: 26 }}
                        >
                            {formatDate(document.modified_date)}
                        </Typography>
                    </Box>

                    {(document.company_names ?? []).length > 0 && (
                        <Box mb={3}>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginBottom: theme.spacing(0.5) }}
                            >
                                Properties
                            </Typography>
                            {(() => {
                                const names = document.company_names ?? []
                                const maxVisible = 2
                                if (names.length <= maxVisible) {
                                    return (
                                        <Typography
                                            variant="body1"
                                            style={{
                                                fontWeight: 500,
                                                marginLeft: 0,
                                            }}
                                        >
                                            {names.join(', ')}
                                        </Typography>
                                    )
                                }
                                const visible = names
                                    .slice(0, maxVisible)
                                    .join(', ')
                                const rest = names.length - maxVisible
                                return (
                                    <Tooltip
                                        title={names.join(', ')}
                                        placement="top"
                                        enterDelay={200}
                                    >
                                        <Typography
                                            variant="body1"
                                            style={{
                                                fontWeight: 500,
                                                marginLeft: 0,
                                                cursor: 'default',
                                            }}
                                        >
                                            {visible}
                                            <Typography
                                                component="span"
                                                color="primary"
                                                style={{
                                                    fontWeight: 600,
                                                    marginLeft: 4,
                                                }}
                                            >
                                                +{rest} more
                                            </Typography>
                                        </Typography>
                                    </Tooltip>
                                )
                            })()}
                        </Box>
                    )}

                    {/* AI Summary */}
                    <Typography
                        variant="overline"
                        style={{
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            marginBottom: theme.spacing(2),
                            display: 'block',
                            marginTop: theme.spacing(2),
                        }}
                    >
                        AI Summary
                    </Typography>

                    <Box
                        style={{
                            backgroundColor: theme.palette.grey[100],
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(2),
                        }}
                    >
                        <Typography
                            variant="body2"
                            style={{
                                lineHeight: 1.7,
                                fontStyle: document.ai_summary
                                    ? 'normal'
                                    : 'italic',
                                color: document.ai_summary
                                    ? theme.palette.text.primary
                                    : theme.palette.text.secondary,
                            }}
                        >
                            {document.ai_summary ||
                                'No summary available yet. The document may still be processing.'}
                        </Typography>
                    </Box>
                </Box>

                {/* Right: Document Viewer */}
                <Box style={{ flex: 1, minWidth: 0 }}>
                    {document.document ? (
                        <iframe
                            src={getViewerUrl()}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                backgroundColor: theme.palette.background.paper,
                            }}
                            title={document.name}
                        />
                    ) : (
                        <Box
                            height="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bgcolor={theme.palette.grey[100]}
                        >
                            <Typography color="textSecondary">
                                No document available
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions
                style={{
                    padding: theme.spacing(2),
                    borderTop: `1px solid ${theme.palette.divider}`,
                    justifyContent: 'space-between',
                }}
            >
                <Button
                    onClick={handleOpenInNewTab}
                    color="default"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                >
                    Open in New Tab
                </Button>
                <Box display="flex" style={{ gap: theme.spacing(1) }}>
                    <Button
                        onClick={onClose}
                        color="default"
                        variant="outlined"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleDownload}
                        color="primary"
                        variant="contained"
                        startIcon={<GetAppIcon />}
                    >
                        Download
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}
